import React, { useState } from "react";
import SEO from "../components/SEO";

const FAQ = () => {
  const faqs = [
    {
      q: "What types of hat patches do you offer?",
      a: "We offer embroidered, woven, PVC, leather, and printed patches for hats. All patches are fully customizable in size, shape, color, and backing.",
    },
    {
      q: "Can I order personalized hat patches in small quantities?",
      a: "Yes! We accept both small and bulk orders. Whether you need a single patch or thousands, we’ve got you covered.",
    },
    {
      q: "How long does production take?",
      a: "Production usually takes 7–10 business days after design approval. Rush options may be available upon request.",
    },
    {
      q: "Do you provide design assistance?",
      a: "Absolutely. Our expert designers will refine or create your patch design at no extra cost.",
    },
    {
      q: "What backings are available for hat patches?",
      a: "We offer iron-on, sew-on, Velcro, adhesive, and heat-seal backings depending on your needs.",
    },
    {
      q: "Can I see a sample before full production?",
      a: "Yes. We provide a digital proof for approval before production begins.",
    },
    {
      q: "What if there is an issue with my order?",
      a: "If there is a production error, submit an Order Issue Form within 45 days of delivery and we’ll make it right.",
    },
    {
      q: "Do you ship internationally?",
      a: "Currently, we primarily serve customers in the United States. For international shipping, please contact our support team.",
    },
  ];

  const [openIndex, setOpenIndex] = useState(null);

  const toggle = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <>
      <SEO
        title="FAQ"
        description="Patch Designers For Hats"
        keywords="Patch Designers For Hats"
      />

      <div className="min-h-screen bg-gradient-to-b from-[#eef6ff] via-white to-[#fff4e8]">
        <div className="mx-auto w-full max-w-[1600px] px-4 py-10 sm:px-6 sm:py-12 lg:px-10 lg:py-14">
          {/* Header */}
          <div className="mx-auto mb-10 max-w-[900px] text-center sm:mb-12 lg:mb-14">
            <span className="mb-5 inline-flex items-center justify-center rounded-full border border-[#f97316]/20 bg-[#f97316]/10 px-5 py-2 text-[13px] font-bold uppercase tracking-[1px] text-[#f97316] shadow-sm sm:text-[14px]">
              Help Center
            </span>

            <h1 className="text-[34px] font-extrabold leading-tight tracking-wide text-[#2b2b2b] sm:text-[46px] lg:text-[58px]">
              Frequently Asked Questions
            </h1>

            <p className="mx-auto mt-5 max-w-[760px] text-[15px] font-medium leading-[28px] text-[#415164] sm:text-[17px] lg:text-[18px] lg:leading-[31px]">
              Find answers to common questions about{" "}
              <strong className="font-extrabold text-[#0a2336]">
                Personalised Patches for Hats
              </strong>
              .
            </p>
          </div>

          {/* FAQ Wrapper */}
          <div className="mx-auto max-w-[1050px] rounded-[26px] border border-[#e5eaf0] bg-white/90 px-4 py-6 shadow-[0_18px_55px_rgba(15,35,54,0.10)] sm:px-6 sm:py-8 lg:px-8">
            <div className="space-y-4">
              {faqs.map((item, index) => {
                const isOpen = openIndex === index;

                return (
                  <div
                    key={index}
                    className={`overflow-hidden rounded-[18px] border transition-all duration-300 ${
                      isOpen
                        ? "border-[#f97316]/40 bg-white shadow-[0_14px_35px_rgba(15,35,54,0.10)]"
                        : "border-[#e5eaf0] bg-[#f8fafc] hover:border-[#f97316]/30 hover:bg-white"
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => toggle(index)}
                      className="flex w-full items-center justify-between gap-4 px-4 py-4 text-left sm:px-5 sm:py-5"
                    >
                      <span className="text-[15px] font-extrabold leading-[24px] text-[#0a2336] sm:text-[17px]">
                        {item.q}
                      </span>

                      <span
                        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[24px] font-light leading-none transition-all duration-300 ${
                          isOpen
                            ? "rotate-45 bg-[#f97316] text-white"
                            : "bg-[#fff7ed] text-[#f97316]"
                        }`}
                      >
                        +
                      </span>
                    </button>

                    <div
                      className={`grid transition-all duration-500 ease-in-out ${
                        isOpen
                          ? "grid-rows-[1fr] opacity-100"
                          : "grid-rows-[0fr] opacity-0"
                      }`}
                    >
                      <div className="overflow-hidden">
                        <div className="border-t border-[#e5eaf0] px-4 pb-5 pt-4 sm:px-5">
                          <p className="text-[14px] font-medium leading-[26px] text-[#415164] sm:text-[15px]">
                            {item.a}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* CTA */}
          <div className="mx-auto mt-10 max-w-[1050px] rounded-[24px] border border-[#e5eaf0] bg-[rgba(31,44,76,0.95)] px-5 py-8 text-center shadow-[0_20px_50px_rgba(15,35,54,0.18)] sm:px-8 lg:px-10">
            <h2 className="text-center text-3xl font-bold tracking-wide text-white md:text-4xl">
              Still Have Questions?
            </h2>

            <p className="mx-auto mt-5 max-w-[680px] text-[15px] font-medium leading-[26px] text-white/80 sm:text-[16px]">
              Our support team is happy to help you with anything you need.
            </p>

            <a
              href="/contact-us"
              className="mt-7 inline-flex rounded-[14px] bg-[#f97316] px-8 py-3.5 text-[15px] font-extrabold uppercase tracking-[0.5px] text-white shadow-[0_14px_32px_rgba(249,115,22,0.25)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#ea580c]"
            >
              Contact Support
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default FAQ;